import { useMemo, useState } from "react";
import { Calendar, TrendingUp } from "lucide-react";

/**
 * Compact per-lot return simulator. Investment amount is locked to the lot's price.
 * Customer slides only the holding period.
 */
export default function LotReturnCalculator({ lotPrice = 100000 }) {
  const [months, setMonths] = useState(5);

  const result = useMemo(() => {
    const baseRate = 0.0225;
    const growthFactor = 1 + (months - 4) * 0.05;
    const profit = Math.round(lotPrice * baseRate * months * growthFactor);
    const platformFee = Math.round(profit * 0.05);
    const net = profit - platformFee;
    const total = lotPrice + net;
    const roi = ((net / lotPrice) * 100).toFixed(1);
    return { profit, platformFee, net, total, roi };
  }, [lotPrice, months]);

  const fmt = (n) => `₹${n.toLocaleString("en-IN")}`;

  return (
    <div className="rounded-2xl overflow-hidden border border-[#E8E0D2] bg-white">
      <div className="px-5 py-4 border-b border-[#E8E0D2] bg-gradient-to-r from-[#FCEDED] to-[#FFF5DA]">
        <div className="flex items-center gap-2 text-[#7A1010] text-xs font-bold uppercase tracking-widest">
          <TrendingUp className="w-3.5 h-3.5" /> Return Simulator
        </div>
        <div className="font-display text-lg text-[#1F1816] mt-1">If I hold this lot for…</div>
      </div>

      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium text-[#1F1816] flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#3F6B3F]" /> Holding period
          </label>
          <span className="font-display text-2xl text-[#3F6B3F]" data-testid="lot-calc-months">{months} mo</span>
        </div>
        <input data-testid="lot-calc-slider" type="range" min={2} max={9} step={1}
          value={months} onChange={(e) => setMonths(+e.target.value)}
          className="w-full gf-slider"
          style={{
            WebkitAppearance: "none",
            height: "6px",
            borderRadius: "9999px",
            background: "linear-gradient(90deg, #3F6B3F 0%, #B8862A 100%)",
            outline: "none",
          }} />
        <div className="flex justify-between text-[11px] text-[#6B5F58] mt-2">
          <span>2 mo</span><span>5 mo</span><span>9 mo</span>
        </div>

        <div className="mt-5 grid grid-cols-3 gap-2 text-center">
          <Box label="Profit" value={fmt(result.profit)} color="#D71921" />
          <Box label="Platform fee" value={fmt(result.platformFee)} color="#6B5F58" />
          <Box label="Net to you" value={fmt(result.net)} color="#3F6B3F" />
        </div>

        <div className="mt-4 rounded-xl text-white p-4 relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #D71921 0%, #B0151E 100%)" }}>
          <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-[#F2C97A]/25 blur-2xl" />
          <div className="relative flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#F2C97A] font-bold">You receive</div>
              <div className="font-display text-3xl mt-1" data-testid="lot-calc-total">{fmt(result.total)}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-white/70">ROI</div>
              <div className="font-display text-2xl text-[#F2C97A]">{result.roi}%</div>
            </div>
          </div>
        </div>
        <p className="text-[10px] text-[#6B5F58] text-center mt-2">* Estimate. Actual returns vary with market & farm operations.</p>
      </div>
    </div>
  );
}

function Box({ label, value, color }) {
  return (
    <div className="bg-[#F8F4E9] rounded-xl p-2.5 border border-[#E8E0D2]">
      <div className="text-[10px] uppercase text-[#6B5F58] tracking-wider">{label}</div>
      <div className="font-display text-base mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}
